import React from "react";
import { Box, Grid, Typography, Switch, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * ステータス表示用コンポーネント（プロジェクト or 転職先用）
 *
 * @param {string} viewerType  - "user" または "company"
 * @param {string} type        - "project" or "job"
 * @param {boolean} isActive   - 受付中かどうか
 * @param {boolean} hasRequest - 希望条件があるか
 * @param {function} [onToggle] - ユーザー側：トグル時のコールバック
 */
const RequestStatus = ({
  viewerType,
  type,
  isActive = false,
  onToggle = () => {},
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  // ----- 文言や色をtypeで分岐 -----
  const labels = {
    project: {
      active: "希望条件を公開中",
      inactive: "希望条件を非公開",
      chipColor: "secondary",
    },
    job: {
      active: "希望条件を公開中",
      inactive: "希望条件を非公開",
      chipColor: "primary",
    },
  };
  const mode = labels[type] || labels.project;

  // 企業側: Chipラベル
  if (viewerType === "company") {
    return (
      <Box
        sx={{
          bgcolor: "#f3f4f6",
          p: { xs: 1, sm: 3 },
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Grid
          container
          alignItems="center"
          spacing={isDesktop ? 2 : 0}
          direction={{ xs: "column", sm: "row" }}
        >
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              textAlign: { xs: "center", sm: "left" },
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{
                fontSize: { xs: "0.9rem", sm: "1.25rem" },
                pl: { xs: 0, sm: 2 },
              }}
            >
              ステータス
            </Typography>
          </Grid>
          <Grid
            item
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: { xs: 0, sm: 2 },
              justifyContent: { xs: "center", sm: "flex-start" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "0.9rem", sm: "1.25rem" },
                fontWeight: "bold",
                mr: 1,
                lineHeight: 1,
                color: "#222",
              }}
            >
              {mode.active}
            </Typography>
            <CheckCircleIcon
              sx={{
                color: theme.palette[mode.chipColor].main,
                fontSize: "1.4em",
                verticalAlign: "middle",
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  }

  // ユーザー側: Switch付き
  return (
    <Box
      sx={{
        bgcolor: "#fff",
        p: { xs: 1, sm: 3 },
        borderRadius: 2,
        mb: 2,
        boxShadow: 1,
      }}
    >
      <Grid
        container
        alignItems="center"
        spacing={isDesktop ? 2 : 0}
        direction={{ xs: "column", sm: "row" }}
      >
        <Grid
          item
          xs={12}
          sm={4}
          sx={{
            textAlign: "center",
            display: "flex",
            justifyContent: { xs: "center", sm: "flex-start" },
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1.25rem" },
              pl: { xs: 0, sm: 2 },
            }}
          >
            ステータス
          </Typography>
        </Grid>
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: { xs: 0, sm: 2 },
            justifyContent: { xs: "center", sm: "flex-start" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              width: { xs: "100%", sm: 300 },
              textAlign: "center",
              fontSize: { xs: "0.95rem", sm: "1.25rem" },
              pl: { xs: 0, sm: 2 },
            }}
          >
            {isActive ? mode.active : mode.inactive}
          </Typography>
          <Switch
            checked={isActive}
            onChange={(e) => {
              onToggle(e);
              const next = e.target.checked;
              window.dispatchEvent(
                new CustomEvent("unread:accepting-changed", {
                  detail:
                    type === "project"
                      ? { acceptingProjects: next }
                      : { acceptingJobs: next },
                })
              );
            }}
            color={mode.chipColor}
            size={isDesktop ? "medium" : "small"}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestStatus;
